import { isDefined } from '@sgme/fp';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId, useValidateAlgo } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectAlgoErrors,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromAlgoTile,
} from '@/store/orders/algoOrders/selectors.ts';

type AlgoRandomizeProps = {
  randomize: boolean | undefined;
  setRandomize: (value: boolean | undefined) => void;
};

export const AlgoEditingRandomize = ({ randomize, setRandomize }: AlgoRandomizeProps) => {
  const tileId = useAlgoTileId();
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const value = randomize ?? (isDefined(orderFromBlotter?.randomize) ? orderFromBlotter?.randomize : undefined);
  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const validateAlgo = useValidateAlgo();

  const handleRandomizeChange = async (newValue: boolean) => {
    track('Order action', 'Randomize changed');
    const response = await validateAlgo({
      randomize: newValue,
    });

    if (response?.data?.validationResponseDetailContent.length === 0) {
      setRandomize(newValue);
    }
  };

  const randomizeError = errors?.randomize;

  return (
    <OrderRow labelId="order.fields.randomize">
      <OmsFieldErrorWrapper errors={randomizeError}>
        <div className="form-check form-check-sm">
          <input
            id="randomize"
            className="form-check-input me-8px"
            type="checkbox"
            checked={value}
            onChange={(e) => handleRandomizeChange(e.target.checked)}
            data-e2e={'randomize-checkbox'}
          />
        </div>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
